import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { isServiceOrLabour } from '../../utils/validation';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { useAuth } from '../Auth/Auth';
import { Navigate } from 'react-router-dom';
import MoonLoader from 'react-spinners/MoonLoader';
import BreadCrumb from '../BreadCrumb';
import { ChartBarIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const pages = [
  { name: 'Analytics & Reports', to: '/analytics', current: false },
  { name: 'Service Demand Trends', to: '/analytics/servicedemand', current: true }
];

const ServiceDemandTrends = () => {
  const { role, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Service demand data
  const [topServices, setTopServices] = useState([]);
  const [serviceTrends, setServiceTrends] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [allServices, setAllServices] = useState([]);

  // Summary stats
  const [totalRequests, setTotalRequests] = useState(0);
  const [uniqueServices, setUniqueServices] = useState(0);
  const [trendingServices, setTrendingServices] = useState([]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  useEffect(() => {
    if (role === 'Service Advisor' || role === 'Admin') {
      fetchServiceDemandData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, selectedYear]);

  const fetchServiceDemandData = async () => {
    setIsLoading(true);
    try {
      // Fetch all jobs and invoices to analyze service demand
      const [jobsSnapshot, invoicesSnapshot] = await Promise.all([
        getDocs(collection(db, 'jobs')),
        getDocs(collection(db, 'newInvoices'))
      ]);

      const serviceMap = {};
      const monthlyServiceMap = {}; // For trend analysis
      const categoryMap = {};
      let totalServiceRequests = 0;

      // Process jobs data
      jobsSnapshot.forEach(doc => {
        const job = doc.data();
        const createdAt = job.createdAt?.toDate?.();

        if (createdAt && createdAt.getFullYear() === selectedYear) {
          const month = createdAt.getMonth();

          // Process service descriptions from job
          if (job.serviceDescription) {
            const serviceName = job.serviceDescription.trim();
            if (serviceName) {
              // Track overall service count
              if (!serviceMap[serviceName]) {
                serviceMap[serviceName] = { count: 0, revenue: 0, category: 'General Service' };
              }
              serviceMap[serviceName].count += 1;
              totalServiceRequests += 1;

              // Track monthly trends
              if (!monthlyServiceMap[month]) {
                monthlyServiceMap[month] = {};
              }
              if (!monthlyServiceMap[month][serviceName]) {
                monthlyServiceMap[month][serviceName] = 0;
              }
              monthlyServiceMap[month][serviceName] += 1;
            }
          }
        }
      });

      // Process invoices to get service revenue and additional service details
      invoicesSnapshot.forEach(doc => {
        const invoice = doc.data();
        const invoiceDate = invoice.invoiceDate?.toDate?.() || invoice.createdAt?.toDate?.();

        if (invoiceDate && invoiceDate.getFullYear() === selectedYear) {
          const month = invoiceDate.getMonth();

          if (invoice.cartItems && Array.isArray(invoice.cartItems)) {
            invoice.cartItems.forEach(item => {
              if (isServiceOrLabour(item)) {
                const serviceName = item.description?.trim();
                if (serviceName) {
                  // Initialize service if not exists
                  if (!serviceMap[serviceName]) {
                    serviceMap[serviceName] = { count: 0, revenue: 0, category: 'Service' };
                  }

                  // Add revenue
                  const itemRevenue = (item.price || 0) * (item.quantity || 0);
                  serviceMap[serviceName].revenue += itemRevenue;

                  // Track count if not already counted from jobs
                  if (!monthlyServiceMap[month] || !monthlyServiceMap[month][serviceName]) {
                    serviceMap[serviceName].count += (item.quantity || 0);
                    totalServiceRequests += (item.quantity || 0);

                    // Track monthly trends
                    if (!monthlyServiceMap[month]) {
                      monthlyServiceMap[month] = {};
                    }
                    if (!monthlyServiceMap[month][serviceName]) {
                      monthlyServiceMap[month][serviceName] = 0;
                    }
                    monthlyServiceMap[month][serviceName] += (item.quantity || 0);
                  }

                  // Categorize services
                  const category = categorizeService(serviceName);
                  serviceMap[serviceName].category = category;

                  if (!categoryMap[category]) {
                    categoryMap[category] = 0;
                  }
                  categoryMap[category] += 1;
                }
              }
            });
          }
        }
      });

      // Convert service map to array and sort by count
      const servicesArray = Object.entries(serviceMap).map(([name, data]) => ({
        name,
        count: data.count,
        revenue: data.revenue,
        category: data.category
      })).sort((a, b) => b.count - a.count);

      // Get top 10 services
      const top10 = servicesArray.slice(0, 10);
      setTopServices(top10);
      setAllServices(servicesArray);
      setTotalRequests(totalServiceRequests);
      setUniqueServices(servicesArray.length);

      // Calculate trending services (comparing last 3 months to previous 3 months)
      const trending = calculateTrendingServices(monthlyServiceMap, servicesArray);
      setTrendingServices(trending);

      // Prepare monthly trend data for top 5 services
      const top5ServiceNames = top10.slice(0, 5).map(s => s.name);
      const trendData = prepareMonthlyTrends(monthlyServiceMap, top5ServiceNames);
      setServiceTrends(trendData);

      // Prepare category breakdown
      const categories = Object.entries(categoryMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
      setCategoryBreakdown(categories);

    } catch (error) {
      logger.error('Error fetching service demand data:', error);
      toast.error('Failed to load service demand data');
    } finally {
      setIsLoading(false);
    }
  };

  const categorizeService = (serviceName) => {
    const name = serviceName.toLowerCase();
    if (name.includes('oil') || name.includes('filter') || name.includes('fluid')) return 'Maintenance';
    if (name.includes('brake') || name.includes('pad') || name.includes('rotor')) return 'Brakes';
    if (name.includes('tire') || name.includes('wheel') || name.includes('alignment')) return 'Tires & Wheels';
    if (name.includes('engine') || name.includes('timing') || name.includes('spark')) return 'Engine';
    if (name.includes('ac') || name.includes('air conditioning') || name.includes('cooling')) return 'AC & Cooling';
    if (name.includes('battery') || name.includes('alternator') || name.includes('electrical')) return 'Electrical';
    if (name.includes('suspension') || name.includes('shock') || name.includes('strut')) return 'Suspension';
    if (name.includes('exhaust') || name.includes('muffler') || name.includes('catalytic')) return 'Exhaust';
    if (name.includes('transmission') || name.includes('clutch') || name.includes('gearbox')) return 'Transmission';
    if (name.includes('diagnostic') || name.includes('inspection') || name.includes('check')) return 'Diagnostics';
    return 'General Service';
  };

  const calculateTrendingServices = (monthlyData, servicesArray) => {
    const currentMonth = new Date().getMonth();
    const lastThreeMonths = [currentMonth, currentMonth - 1, currentMonth - 2].filter(m => m >= 0);
    const previousThreeMonths = [currentMonth - 3, currentMonth - 4, currentMonth - 5].filter(m => m >= 0);

    const trends = servicesArray.map(service => {
      const recentCount = lastThreeMonths.reduce((sum, month) => {
        return sum + (monthlyData[month]?.[service.name] || 0);
      }, 0);

      const previousCount = previousThreeMonths.reduce((sum, month) => {
        return sum + (monthlyData[month]?.[service.name] || 0);
      }, 0);

      const change = previousCount > 0 ? ((recentCount - previousCount) / previousCount) * 100 : 0;

      return {
        name: service.name,
        change: Math.round(change),
        recentCount
      };
    });

    // Get top 5 trending (positive change) and sort by change percentage
    return trends
      .filter(t => t.change > 0)
      .sort((a, b) => b.change - a.change)
      .slice(0, 5);
  };

  const prepareMonthlyTrends = (monthlyData, serviceNames) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const datasets = serviceNames.map((serviceName, index) => {
      const colors = [
        'rgb(99, 102, 241)',   // Indigo
        'rgb(16, 185, 129)',   // Emerald
        'rgb(249, 115, 22)',   // Orange
        'rgb(236, 72, 153)',   // Pink
        'rgb(139, 92, 246)'    // Purple
      ];

      const data = months.map((_, monthIndex) => {
        return monthlyData[monthIndex]?.[serviceName] || 0;
      });

      return {
        label: serviceName,
        data: data,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        tension: 0.4
      };
    });

    return {
      labels: months,
      datasets: datasets
    };
  };

  // Chart configurations
  const topServicesChartData = {
    labels: topServices.map(s => s.name.length > 30 ? s.name.substring(0, 30) + '...' : s.name),
    datasets: [{
      label: 'Number of Requests',
      data: topServices.map(s => s.count),
      backgroundColor: 'rgba(99, 102, 241, 0.8)',
      borderColor: 'rgb(99, 102, 241)',
      borderWidth: 1
    }]
  };

  const topServicesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          afterLabel: function(context) {
            const service = topServices[context.dataIndex];
            return `Revenue: ${formatCurrency(service.revenue)}\nCategory: ${service.category}`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, grid: { display: false } },
      y: { grid: { display: false } }
    }
  };

  const trendLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 15, usePointStyle: true }
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const categoryDoughnutData = {
    labels: categoryBreakdown.map(c => c.name),
    datasets: [{
      data: categoryBreakdown.map(c => c.count),
      backgroundColor: [
        'rgba(99, 102, 241, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(249, 115, 22, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(59, 130, 246, 0.8)',
        'rgba(245, 158, 11, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(20, 184, 166, 0.8)',
        'rgba(168, 85, 247, 0.8)'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const categoryDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { padding: 10, usePointStyle: true }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Service Advisor' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <ChartBarIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Service Demand Trends</h1>
                <p className="text-sm text-gray-600 mt-1">Analyze customer service requests and demand patterns</p>
              </div>
            </div>

            {/* Year Selector */}
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-xl shadow-md">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <MoonLoader size={50} color="#6366F1" />
              <p className="mt-4 text-sm font-medium text-gray-600">Loading service demand data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Service Requests */}
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Service Requests</p>
                    <h3 className="text-3xl font-bold mt-1">{totalRequests.toLocaleString()}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ChartBarIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-blue-100 text-xs">in {selectedYear}</p>
              </div>

              {/* Unique Service Types */}
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Unique Service Types</p>
                    <h3 className="text-3xl font-bold mt-1">{uniqueServices}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                </div>
                <p className="text-emerald-100 text-xs">Different services offered</p>
              </div>

              {/* Trending Services */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Trending Services</p>
                    <h3 className="text-3xl font-bold mt-1">{trendingServices.length}</h3>
                  </div>
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <ArrowTrendingUpIcon className="w-7 h-7" />
                  </div>
                </div>
                <p className="text-orange-100 text-xs">Growing in demand</p>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Top 10 Services Chart */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Top 10 Most Requested Services</h3>
                  <p className="text-sm text-gray-600 mt-1">Services with highest customer demand in {selectedYear}</p>
                </div>
                <div style={{ height: '400px' }}>
                  {topServices.length > 0 ? (
                    <Bar data={topServicesChartData} options={topServicesChartOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No service data available for {selectedYear}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Service Category Distribution</h3>
                  <p className="text-sm text-gray-600 mt-1">Breakdown by service category</p>
                </div>
                <div style={{ height: '400px' }}>
                  {categoryBreakdown.length > 0 ? (
                    <Doughnut data={categoryDoughnutData} options={categoryDoughnutOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p>No category data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Monthly Trends */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900">Monthly Service Demand Trends</h3>
                <p className="text-sm text-gray-600 mt-1">Request patterns for top 5 services throughout {selectedYear}</p>
              </div>
              <div style={{ height: '350px' }}>
                {serviceTrends.datasets?.length > 0 ? (
                  <Line data={serviceTrends} options={trendLineChartOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No trend data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Trending Services Section */}
            {trendingServices.length > 0 && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl shadow-lg p-6 mb-8 border border-orange-200">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <ArrowTrendingUpIcon className="w-6 h-6 text-orange-600" />
                    Trending Services (Last 3 Months)
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Services showing increased demand compared to previous period</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {trendingServices.map((service, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-orange-100">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{service.name}</h4>
                        <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-lg text-xs font-bold">
                          <ArrowTrendingUpIcon className="w-3 h-3" />
                          {service.change}%
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">{service.recentCount} requests in last 3 months</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Services Table */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
                <h3 className="text-xl font-bold text-white">All Services ({allServices.length})</h3>
                <p className="text-blue-100 text-sm mt-1">Complete breakdown of service requests and revenue</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg per Request</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allServices.length > 0 ? (
                      allServices.map((service, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">{service.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {service.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {service.count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {formatCurrency(service.revenue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                            {service.count > 0 ? formatCurrency(service.revenue / service.count) : '₦0'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <ChartBarIcon className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm">No service data available for {selectedYear}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceDemandTrends;
